import { useDictionaryContext } from 'application/contexts/DictionaryContext';
import { usePageContext } from 'application/contexts/PageContext';
import { IconText } from 'designsystem/components/2-molecules/IconText';
import { DateRangePickerProps } from 'designsystem/components/2-molecules/Inputs';
import { ListFilters, ListFiltersProps } from 'designsystem/components/2-molecules/ListFilters';
import { SearchBoxProps } from 'designsystem/components/2-molecules/SearchBox';
import { CatalogPage } from 'designsystem/components/5-templates/CatalogPage';
import { RSSMeta } from 'features/Layouts/Layout/RSSMeta';
import { ModuleFeature } from 'features/Modules/DynamicModule';
import { LinkFeature } from 'features/Modules/Link';
import { TopBannerFeature } from 'features/Modules/TopBanner';
import { cludoTagsName, cludoTypesName } from 'hooks/useCludo';
import RSS from 'icons/rss.svg';
import getConfig from 'next/config';
import { useEffect, useState } from 'react';

export interface CatalogListItemType {
	date?: string;
	heading: string;
	link: React.ReactElement;
	teaser?: string;
	image?: UI.Image;
	tags?: {
		id?: number;
		culture?: string;
		name?: string;
		sortOrder?: number;
	}[];
	types?: string[];
	expirationDate?: string;
}

export interface CatalogPageFeatureProps extends Content.CatalogBasePage, Content.CatalogList {
	hideImages?: boolean;
	rssFeedUrl: string;
	choosenDropdownFilters?: ListFiltersProps['choosenDropdownFilters'];
	searchWord?: SearchBoxProps['searchWord'];
	dateRange?: DateRangePickerProps['callBack'];
}

export const CatalogPageFeature: React.FC<CatalogPageFeatureProps> = ({
	additionalTop,
	additionalPageContent,
	bannerContent,
	catalogContent,
	hideImages,
	heading,
	teaser,
	filters,
	noItemsMessage,
	catalogListItems,
	rssConfig,
	rssFeedUrl,
	choosenDropdownFilters,
	searchWord,
	dateRange,
}) => {
	const dictionary = useDictionaryContext();
	const pageContext = usePageContext();
	const { DOMAIN_PROTOCOL } = getConfig()?.publicRuntimeConfig ?? {};

	const [showHistoricItems, setShowHistoricItems] = useState(false);
	const [showHistoricCheckbox, setShowHistoricCheckbox] = useState(false);
	const [filterTags, setFilterTags] = useState([]);
	const [filterTypes, setFilterTypes] = useState([]);
	const [catalogResult, setCatalogResult] = useState<CatalogListItemType[]>(null);

	const { hideDates } = pageContext?.configuration ?? {};

	const getFilterNames = (filterNodes): string[] => {
		if (!filterNodes) return [];
		return filterNodes
			.map((filter) => {
				const preFilters = filter?.content?.properties?.preSelectedFilters;
				if (preFilters) return [];
				const filterTags = filter.content.properties.folder[0].properties.filter((option) => {
					if (option.contentType === 'tagsGroupFolder' || option.contentType === 'typesGroupFolder') return false;
					return true;
				});
				const filterTagNames = filterTags?.map((filter) => filter.name);
				return filterTagNames;
			})
			.flat();
	};

	useEffect(() => {
		if (!catalogListItems) return;
		const catalogItems = catalogListItems?.map((result): CatalogListItemType => {
			const { expirationDate, link, date, image } = result ?? {};
			const expirationDateWithoutTime = expirationDate?.split('T')[0];
			return {
				...result,
				image: hideImages ? null : image,
				date: hideDates ? null : date,
				link: <LinkFeature url={link.url} title={link.name} />,
				expirationDate: new Date(expirationDateWithoutTime).getFullYear() === 1 ? undefined : expirationDateWithoutTime,
			};
		});
		const tagFolders = filters?.items?.filter((node) => node?.content?.contentType === 'tagFolderPicker');
		const typeFolders = filters?.items?.filter((node) => node?.content?.contentType === 'typesFolderPicker');
		const tags = getFilterNames(tagFolders);
		const types = getFilterNames(typeFolders);

		setFilterTags(tags);
		setFilterTypes(types);

		setShowHistoricCheckbox(catalogItems.some((item) => item.expirationDate));
		setCatalogResult(catalogItems);
	}, [catalogListItems, filters, hideDates, hideImages]);

	const showHistoricCatalog = (catalogList: CatalogListItemType[], show: boolean): CatalogListItemType[] => {
		if (!catalogList) return;
		if (!catalogList.some((cr) => cr.expirationDate)) return catalogList;

		return catalogList.filter((li) => {
			const liExpirationDate = new Date(li.expirationDate);
			if (show) {
				return li.expirationDate && liExpirationDate.getTime() < new Date().getTime();
			} else {
				return !li.expirationDate || liExpirationDate.getTime() > new Date().getTime();
			}
		});
	};

	const rssFeedUrlSanitized = rssFeedUrl?.replace(`${DOMAIN_PROTOCOL}://${pageContext.host}`, '');

	return (
		<>
			{rssConfig && rssConfig?.items.length === 1 && <RSSMeta title={rssConfig.items[0].content.properties.title} url={rssFeedUrlSanitized} />}
			<CatalogPage
				additionalTop={additionalTop}
				additionalPageContent={additionalPageContent}
				heading={heading}
				teaser={teaser}
				noItemsMessage={noItemsMessage}
				filters={
					<ListFilters
						filters={[
							{
								groupId: filterTags?.length ? cludoTagsName : null,
								label: filterTags?.length ? dictionary.getLabel('Category') : null,
								options: filterTags,
							},
							{
								groupId: filterTypes?.length ? cludoTypesName : null,
								label: filterTypes?.length ? dictionary.getLabel('Type') : null,
								options: filterTypes,
							},
						]}
						removeFilter={dictionary.getAction('RemoveFilter')}
						onShowHistoricChange={showHistoricCheckbox ? (v) => setShowHistoricItems(v) : undefined}
						historicCheckboxLabel={dictionary.getLabel('HistoricalArchive')}
						choosenDropdownFilters={choosenDropdownFilters ? choosenDropdownFilters : null}
						searchWord={searchWord ? searchWord : null}
						dateRange={dateRange ? dateRange : null}
						texts={{
							searchPlaceholder: dictionary.getSearch('Placeholder'),
							searchActionAriaLabel: dictionary.getSearch('Search'),
							nextMonth: dictionary.getLabel('NextMonth'),
							previousMonth: dictionary.getLabel('PreviousMonth'),
							dateRangePlaceholder: dictionary.getLabel('DateRangePlaceholder'),
						}}
					/>
				}
				catalogList={showHistoricCatalog(catalogResult, showHistoricItems)} // TODO: Omit function and handle in useEffect
				ctaLink={
					rssConfig && rssConfig?.items.length === 1 ? (
						<LinkFeature url={rssFeedUrlSanitized} target="blank">
							<IconText icon={<RSS />} iconSize="sm" text={dictionary.getAction('SubscribeWithRSS')} />
						</LinkFeature>
					) : null
				}
				pageSizeOptions={[10, 20]}
				pageSizeAllOption={dictionary.getLabel('All')}
				pageSizeOptionsDefaultValueIndex={1}
				paginationErrorText={dictionary.getError('CatalogPagination')}
				paginationTexts={{
					totalResultsText: dictionary.getLabel('TotalResults'),
					showText: dictionary.getAction('Show').toLowerCase(),
					resultsPerPageText: dictionary.getLabel('ResultsPerPage').toLowerCase(),
					pageText: dictionary.getLabel('Page'),
					outOfText: dictionary.getLabel('OutOf').toLowerCase(),
					pagesText: dictionary.getLabel('Pages').toLowerCase(),
				}}
				paginationAriaLabels={{
					selectInputAriaLabel: dictionary.getLabel('NumberOfResultsPerPage'),
					textInputAriaLabel: dictionary.getLabel('CurrentPage'),
					prevButtonAriaLabel: dictionary.getLabel('PreviousPage'),
					nextButtonAriaLabel: dictionary.getLabel('NextPage'),
				}}
				loadingText={dictionary.getServiceInfo('FetchingData')}
				topBanner={bannerContent?.items?.length > 0 && <TopBannerFeature bannerContent={bannerContent} />}
				catalogContent={
					catalogContent ? catalogContent.items.map((content, key) => <ModuleFeature contentModule={content} key={key} />) : null
				}
			/>
		</>
	);
};
